import { supabase } from '../config/supabase';
import type { LookupKey, LookupState, Option, UserPreferences } from '../types';
import { comparePlayerNames, defaultUserPreferences, formatPlayerName } from '../utils/playerPreferences';

const emptyLookups: LookupState = {
  app_users: [],
  workspaces: [],
  organizations: [],
  league_configurations: [],
  facilities: [],
  teams: [],
  players: [],
  league_configuration_positions: [],
  recommendation_strategy_profiles: [],
};

function option(label: string | null | undefined, value: string, extra: Partial<Option> = {}): Option {
  return { value, label: label || value, ...extra };
}

const organizationSortOrder = ['TCD', 'USTA', 'UTR', 'Other'];

function sortOrganizationOptions(a: Option, b: Option): number {
  const aIndex = organizationSortOrder.findIndex((name) => name.toLowerCase() === a.label.toLowerCase());
  const bIndex = organizationSortOrder.findIndex((name) => name.toLowerCase() === b.label.toLowerCase());
  const normalizedA = aIndex === -1 ? organizationSortOrder.length : aIndex;
  const normalizedB = bIndex === -1 ? organizationSortOrder.length : bIndex;
  return normalizedA - normalizedB || a.label.localeCompare(b.label);
}

function isMissingWorkspaceSchema(message: string | undefined): boolean {
  return Boolean(message && (
    /could not find the table.*workspaces/i.test(message)
    || /relation .*workspaces.* does not exist/i.test(message)
  ));
}

export async function loadLookups(preferences: UserPreferences = defaultUserPreferences): Promise<LookupState> {
  const [
    appUsers,
    workspaces,
    organizations,
    leagueConfigurations,
    facilities,
    teams,
    players,
    positions,
    recommendationStrategyProfiles,
  ] = await Promise.all([
    supabase.from('app_users').select('id, display_name, email').is('deleted_at', null).order('display_name'),
    supabase.from('workspaces').select('id, name').eq('active_status', true).order('name'),
    supabase.from('organizations').select('id, name').is('deleted_at', null).order('name'),
    supabase.from('league_configurations').select('id, workspace_id, configuration_name, match_format_name, is_active').is('deleted_at', null).order('configuration_name'),
    supabase.from('facilities').select('id, facility_name').is('deleted_at', null).order('facility_name'),
    supabase.from('teams').select('id, team_name, team_color, season_label, workspace_id, team_gender_type, player_rating_basis, is_active').is('deleted_at', null).order('team_name'),
    supabase.from('players').select('id, first_name, last_name').is('deleted_at', null),
    supabase.from('league_configuration_positions').select('id, position_code, display_name').is('deleted_at', null).order('display_order'),
    supabase.from('recommendation_strategy_profiles').select('id, name, version').eq('is_active', true).is('deleted_at', null).order('name'),
  ]);

  const failures = [appUsers, organizations, leagueConfigurations, facilities, teams, players, positions].filter((result) => result.error);
  if (workspaces.error && !isMissingWorkspaceSchema(workspaces.error.message)) failures.push(workspaces);
  if (recommendationStrategyProfiles.error && !/recommendation_strategy_profiles/i.test(recommendationStrategyProfiles.error.message)) failures.push(recommendationStrategyProfiles);
  if (failures.length > 0) {
    throw new Error(failures.map((result) => result.error?.message).join('; '));
  }

  return {
    ...emptyLookups,
    app_users: (appUsers.data || []).map((row) => option(`${row.display_name}${row.email ? ` (${row.email})` : ''}`, row.id)),
    workspaces: (workspaces.data || []).map((row) => option(row.name, row.id)),
    organizations: (organizations.data || []).map((row) => option(row.name, row.id)).sort(sortOrganizationOptions),
    league_configurations: (leagueConfigurations.data || []).map((row) => option(row.configuration_name || row.match_format_name, row.id, {
      workspace_id: row.workspace_id,
      is_active: row.is_active,
    })),
    facilities: (facilities.data || []).map((row) => option(row.facility_name, row.id)),
    teams: (teams.data || []).map((row) => option(row.team_name, row.id, {
      team_color: row.team_color || 'gray',
      season_label: row.season_label,
      workspace_id: row.workspace_id,
      team_gender_type: row.team_gender_type,
      player_rating_basis: row.player_rating_basis || 'edr',
      is_active: row.is_active !== false,
    })),
    players: (players.data || [])
      .sort((a, b) => comparePlayerNames(a, b, preferences))
      .map((row) => option(formatPlayerName(row, preferences), row.id)),
    league_configuration_positions: (positions.data || []).map((row) => option(`${row.position_code} - ${row.display_name}`, row.id)),
    recommendation_strategy_profiles: (recommendationStrategyProfiles.data || []).map((row) => option(`${row.name} v${row.version || 1}`, row.id)),
  };
}

export function lookupLabel(lookups: LookupState, key: LookupKey | undefined, value: unknown): string {
  if (!key || typeof value !== 'string') return value == null ? '' : String(value);
  return lookups[key].find((item) => item.value === value)?.label || value;
}
